import { queryOptions, useQuery } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

/**
 * One recording plays at a time, wherever it was started. A library is a
 * wall of sounds, and pressing a second one while the first carries on is
 * never what was meant, so every player shares this one element and asks it
 * whether it is the one playing.
 */
interface Playing {
  key: string;
  /** The recording's length as processing measured it. A browser streaming Ogg can report it as infinite. */
  seconds: number;
  playing: boolean;
  /** How far through, from 0 to 1. */
  progress: number;
}

let audio: HTMLAudioElement | null = null;
let current: Playing | null = null;
const listeners = new Set<() => void>();

function publish(next: Playing | null) {
  current = next;
  for (const listener of listeners) listener();
}

function element(): HTMLAudioElement {
  if (audio) return audio;
  const created = new Audio();
  created.preload = "none";
  const report = () => {
    if (!current) return;
    const seconds = Number.isFinite(created.duration) ? created.duration : current.seconds;
    publish({
      ...current,
      playing: !created.paused,
      progress: seconds > 0 ? Math.min(1, created.currentTime / seconds) : 0,
    });
  };
  created.addEventListener("timeupdate", report);
  created.addEventListener("play", report);
  created.addEventListener("pause", report);
  created.addEventListener("ended", () => {
    if (current) publish({ ...current, playing: false, progress: 0 });
  });
  audio = created;
  return created;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Plays, pauses and seeks one recording. `key` is what makes it the same
 * recording across renders — the media version, since a draft's URL is
 * signed again every hour and would otherwise count as a different sound.
 */
export function usePlayback(key: string, url: string, seconds: number) {
  const state = useSyncExternalStore(subscribe, () => current);
  const mine = state?.key === key ? state : null;

  const start = useCallback(() => {
    const player = element();
    if (current?.key !== key) {
      player.src = url;
      publish({ key, seconds, playing: false, progress: 0 });
    }
    void player.play().catch(() => undefined);
  }, [key, url, seconds]);

  const toggle = useCallback(() => {
    if (current?.key === key && current.playing) element().pause();
    else start();
  }, [key, start]);

  const seek = useCallback(
    (fraction: number) => {
      const player = element();
      if (current?.key !== key) start();
      const apply = () => {
        player.currentTime =
          fraction * (Number.isFinite(player.duration) ? player.duration : seconds);
      };
      if (player.readyState >= 1) apply();
      else player.addEventListener("loadedmetadata", apply, { once: true });
    },
    [key, start, seconds],
  );

  return {
    playing: mine?.playing ?? false,
    progress: mine?.progress ?? 0,
    /** Whether this is the recording in the player: playing, or paused part of the way in. */
    started: mine !== null && (mine.playing || mine.progress > 0),
    toggle,
    seek,
  };
}

/** Stops whatever is playing, as leaving the screen it was started from should. */
export function stopPlayback() {
  audio?.pause();
}

/**
 * The 1,024 peaks of a recording, fetched once per version. They are drawn
 * at whatever number of bars there is room for, so this is the raw list.
 */
const waveformQuery = (key: string, url: string) =>
  queryOptions({
    queryKey: ["asset-waveforms", key],
    queryFn: async ({ signal }) => {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Waveform answered ${response.status}.`);
      return (await response.json()) as number[];
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });

export function useWaveform(key: string, url: string) {
  return useQuery(waveformQuery(key, url)).data;
}

/**
 * The loudest peak in each of `bars` equal stretches, from 0 to 1 of the
 * recording's own loudest. Every recording is brought to the same loudness
 * when it is processed, which leaves most peaks well under full scale, so
 * drawn against full scale every waveform would be the same flat line.
 */
export function barsOf(peaks: number[] | undefined, bars: number): number[] {
  if (!peaks || peaks.length === 0) return Array<number>(bars).fill(0.08);
  const ceiling = Math.max(1, ...peaks);
  const result: number[] = [];
  for (let bar = 0; bar < bars; bar++) {
    const start = Math.floor((bar * peaks.length) / bars);
    const end = Math.max(start + 1, Math.floor(((bar + 1) * peaks.length) / bars));
    let loudest = 0;
    for (let at = start; at < end; at++) loudest = Math.max(loudest, peaks[at] ?? 0);
    result.push(Math.max(0.06, loudest / ceiling));
  }
  return result;
}
